import {useCallback, useContext, useEffect, useRef} from 'react';

import {useWidget} from '@stripe-internal/conversations-ui';

import {useIntl} from 'react-intl';
import {filter} from 'rxjs';
import {Screen, ScreenName, useScreen} from '../Providers/ScreenProvider';
import {BlankScreen} from './BlankScreen';
import {ChatScreen} from './ChatScreen';
import {UnrecoverableErrorScreen} from './UnrecoverableErrorScreen';
import {SplashScreen} from './SplashScreen';
import {RequestACallScreen} from './RequestACallScreen';
import {RequestACallConfirmationScreen} from './RequestACallConfirmationScreen';
import {RequestACallConfirmationInlineScreen} from './RequestACallConfirmationInlineScreen';
import {CSATScreen} from './CSATScreen/CSATScreen';
import {TestScreen} from './TestScreen';
import {SessionRetryScreen} from './SessionRetryScreen';
import {useProfile} from '../Providers/ProfileProvider';
import {ConfigContext} from '../Providers/ConfigProvider';
import {useAnalyticsReporter} from '../helpers/analytics';
import {useObservability} from '../Providers/ObservabilityProvider';
import {DotComCSATScreen} from './CSATScreen/DotComtCSATScreen';

function useFirstOpenAnalytic() {
  const {audience, entrypoint} = useContext(ConfigContext);
  const analyticsReporter = useAnalyticsReporter();
  const {screen, transitionTo} = useScreen();
  const widget = useWidget();

  const {locale} = useIntl();
  const {analytics} = useObservability();
  const {profile} = useProfile();
  const {visitor, featureFlags, merchant} = profile;

  const analyticFired = useRef<boolean>(false);

  const emitWidgetShownAnalytic = useCallback(async () => {
    if (analyticFired.current) {
      return;
    }
    analyticFired.current = true;

    const {cid: analyticsCid} = await analyticsReporter.getClientIDs();
    const analyticsEventId = await analyticsReporter.getPreviousEventId();
    const triggerParams = widget.triggerParams();

    analytics.action('support_conversations_widget_shown', {
      enableUniversalHelp: featureFlags.enableUniversalHelp,
      audience,
      source: entrypoint,
      locale,
      sessionType: visitor.sessionType,
      merchantId: merchant?.id,
      userId: visitor.id,
      analyticsCid,
      analyticsEventId,
      triggerPoint: triggerParams?.triggerPoint,
    });
  }, [
    analytics,
    analyticsReporter,
    audience,
    entrypoint,
    featureFlags.enableUniversalHelp,
    locale,
    merchant?.id,
    visitor.id,
    visitor.sessionType,
    widget,
  ]);

  useEffect(() => {
    if (analyticFired.current) {
      return;
    }

    if (widget.visibility() === 'open') {
      emitWidgetShownAnalytic();
      return;
    }

    const opened$ = widget
      .visibilityObservable()
      .pipe(filter((v) => v === 'open'));

    const sub = opened$.subscribe(() => {
      emitWidgetShownAnalytic();
    });

    return () => sub.unsubscribe();
  }, [audience, screen, widget, transitionTo, emitWidgetShownAnalytic]);
}

function ScreenComponent({screen}: {screen: Screen}) {
  useFirstOpenAnalytic();

  switch (screen.name) {
    case ScreenName.Blank: {
      return <BlankScreen />;
    }
    case ScreenName.Splash: {
      return <SplashScreen />;
    }
    case ScreenName.Chat: {
      return <ChatScreen {...screen.props} />;
    }
    case ScreenName.RequestACall: {
      return <RequestACallScreen {...screen.props} />;
    }
    case ScreenName.RequestACallConfirmation: {
      return <RequestACallConfirmationScreen {...screen.props} />;
    }
    case ScreenName.RequestACallConfirmationInline: {
      return <RequestACallConfirmationInlineScreen {...screen.props} />;
    }
    case ScreenName.CSAT: {
      return <CSATScreen {...screen.props} />;
    }
    case ScreenName.DotComCSAT: {
      return <DotComCSATScreen {...screen.props} />;
    }
    case ScreenName.Error: {
      return <UnrecoverableErrorScreen {...screen.props} />;
    }
    case ScreenName.Test: {
      return <TestScreen />;
    }
    default: {
      return <UnrecoverableErrorScreen />;
    }
  }
}

export function CurrentScreen() {
  const {screen} = useScreen();

  if (screen.name === ScreenName.SessionRetry) {
    return <SessionRetryScreen />;
  }

  return <ScreenComponent screen={screen} />;
}
